import { useEffect, useMemo, useState } from 'react';
import { ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AddressBookSelectorDialog } from '@/components/shared/address-book-selector-dialog';
import { useListAddressBookEntries } from '@/hooks/organization/use-list-address-book';
import { Button } from '@/components/ui/button';

export type ContractAddressSelectorValue =
  | { type: 'any' }
  | { type: 'specific'; address: string; name?: string | null };

type ContractAddressSelectorProps = {
  value: ContractAddressSelectorValue;
  onValueChange: (value: ContractAddressSelectorValue) => void;
  contractName: string | null;
  isLoadingContract: boolean;
  status: 'default' | 'warning';
};

export function ContractAddressSelector({
  value,
  onValueChange,
  contractName,
  isLoadingContract,
  status,
}: ContractAddressSelectorProps) {
  const [open, setOpen] = useState(false);
  const selectedAddress = value.type === 'specific' ? value.address : null;
  const selectedLabel = value.type === 'specific' ? (value.name ?? null) : null;
  const {
    entries: addressBookEntries,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useListAddressBookEntries({
    enabled: value.type === 'specific',
    type: 'CONTRACT',
    pagination: { limit: 100 },
  });

  const matchingEntry = useMemo(() => {
    if (!selectedAddress) return undefined;
    return addressBookEntries.find(
      (entry) => entry.address.toLowerCase() === selectedAddress.toLowerCase()
    );
  }, [addressBookEntries, selectedAddress]);

  useEffect(() => {
    if (
      value.type !== 'specific' ||
      selectedLabel ||
      matchingEntry ||
      !hasMore ||
      isLoadingMore
    ) {
      return;
    }

    void loadMore();
  }, [
    hasMore,
    isLoadingMore,
    loadMore,
    matchingEntry,
    selectedLabel,
    value.type,
  ]);

  const getDisplayValue = () => {
    if (value.type === 'any') return 'Any contract';
    if (isLoadingContract) return 'Loading contract...';
    if (matchingEntry?.name) return matchingEntry.name;
    if (selectedLabel && selectedLabel.trim()) return selectedLabel;
    if (contractName && contractName.trim()) return contractName;
    const address = value.address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        size="lg"
        className={cn(
          'w-full justify-between bg-background border-slate-300 border text-base',
          status === 'warning' && 'border-red-500 focus-visible:ring-red-500'
        )}
        onClick={() => setOpen(true)}
      >
        <span className="flex items-center gap-2">
          {isLoadingContract && value.type === 'specific' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : null}
          {getDisplayValue()}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <AddressBookSelectorDialog
        open={open}
        onOpenChange={setOpen}
        mode="single"
        title="Select contract"
        description="Choose a contract from the address book, or save a new contract address before using it."
        selectedAddress={selectedAddress}
        onAddressSelect={(address, label) => {
          onValueChange({ type: 'specific', address, name: label });
        }}
        onAddressClear={() => {
          onValueChange({ type: 'any' });
        }}
        typeFilter="CONTRACT"
        hideFilters
        searchPlaceholder="Search address book or paste contract address"
        singleSelectionRequiresConfirm
      />
    </>
  );
}
